import { useMemo, useCallback } from "react";
import { Wrench, Layers, GripVertical, Sparkles } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ComponentPicker } from "./ComponentPicker";
import { ModelImporter } from "./ModelImporter";
import { ActiveComponentCard } from "./ActiveComponentCard";
import { SortableComponentCard } from "./SortableComponentCard";
import { CustomTemplateWarning, useCustomTemplateValidation } from "./CustomTemplateWarning";
import type { Configuration, ConfigComponent } from "./types";

interface CustomConfigurationBuilderProps {
  configuration: Configuration;
  onConfigurationChange: (config: Configuration) => void;
  customerLastName?: string;
}

export function CustomConfigurationBuilder({
  configuration,
  onConfigurationChange,
  customerLastName
}: CustomConfigurationBuilderProps) {
  // Separate active (quantity > 0) and available (quantity = 0) components
  const activeComponents = useMemo(() => 
    configuration.components.filter(c => c.quantity > 0),
    [configuration.components]
  );

  // Available = template components (quantity === 0) that still have room for more instances
  const availableComponents = useMemo(() => {
    // Count active instances per componentTypeId
    const activeCountByType = new Map<string, number>();
    for (const c of configuration.components) {
      if (c.quantity > 0) {
        activeCountByType.set(c.componentTypeId, (activeCountByType.get(c.componentTypeId) || 0) + 1);
      }
    }
    
    // Template components (quantity === 0) are always candidates
    return configuration.components.filter(c => {
      if (c.quantity !== 0) return false;
      const activeCount = activeCountByType.get(c.componentTypeId) || 0;
      // Show if total active instances haven't reached maxQuantity
      return activeCount < c.maxQuantity;
    });
  }, [configuration.components]);

  // Track which component types are already active (for "add another" label)
  const activeComponentTypeIds = useMemo(() => 
    new Set(activeComponents.map(c => c.componentTypeId)),
    [activeComponents]
  );

  // Validation warnings
  const warnings = useCustomTemplateValidation(configuration.components);

  // Add component: activate template or create new instance
  const handleAddComponent = (componentId: string) => {
    const template = configuration.components.find(c => c.id === componentId);
    if (!template) return;

    // If this template has quantity === 0 (never activated), just activate it
    const existingActive = configuration.components.filter(
      c => c.componentTypeId === template.componentTypeId && c.quantity > 0
    );

    if (existingActive.length === 0) {
      // First instance — activate the template
      onConfigurationChange({
        ...configuration,
        components: configuration.components.map(c =>
          c.id === componentId 
            ? { ...c, quantity: 1 }
            : c
        )
      });
    } else {
      // Additional instance — clone as a new entry with unique id
      const newInstance: ConfigComponent = {
        ...template,
        id: `${template.id}-${Date.now()}`,
        quantity: 1,
        attributes: template.attributes.map(a => ({ ...a, value: a.value })),
        allowedMaterials: [...template.allowedMaterials],
      };
      onConfigurationChange({
        ...configuration,
        components: [...configuration.components, newInstance]
      });
    }
  };

  // Remove component: set quantity to 0 (template) or remove cloned instance
  const handleRemoveComponent = (componentId: string) => {
    const component = configuration.components.find(c => c.id === componentId);
    if (!component) return;

    // If this is a cloned instance (id contains '-'), remove it entirely
    const isClone = componentId.includes('-') && componentId !== component.modelComponentId;
    if (isClone) {
      onConfigurationChange({
        ...configuration,
        components: configuration.components.filter(c => c.id !== componentId)
      });
    } else {
      // Original template — set quantity to 0
      onConfigurationChange({
        ...configuration,
        components: configuration.components.map(c =>
          c.id === componentId 
            ? { ...c, quantity: 0 }
            : c
        )
      });
    }
  };

  // Update quantity (clamped to min 1, max maxQuantity for active components)
  const handleQuantityChange = (componentId: string, quantity: number) => {
    const component = configuration.components.find(c => c.id === componentId);
    if (!component) return;

    const clampedQuantity = Math.max(1, Math.min(quantity, component.maxQuantity));
    
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, quantity: clampedQuantity }
          : c
      )
    });
  };

  // Update material
  const handleMaterialChange = (componentId: string, materialId: string) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, materialId }
          : c
      )
    });
  };

  // Update dimension
  const handleDimensionChange = (
    componentId: string, 
    dimension: 'width' | 'height' | 'thickness', 
    value: number | null
  ) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c =>
        c.id === componentId 
          ? { ...c, [dimension]: value }
          : c
      )
    });
  };

  // Update attribute
  const handleAttributeChange = (
    componentId: string,
    attributeId: string,
    value: string | number | null
  ) => {
    onConfigurationChange({
      ...configuration,
      components: configuration.components.map(c => {
        if (c.id === componentId) {
          return {
            ...c,
            attributes: c.attributes.map(attr =>
              attr.id === attributeId ? { ...attr, value } : attr
            )
          };
        }
        return c;
      })
    });
  };

  // Handle custom name change
  const handleCustomNameChange = (value: string) => {
    onConfigurationChange({
      ...configuration,
      customName: value
    });
  };

  // Smart default: suggest "Hautakivi [Sukunimi]"
  const handleSuggestName = () => {
    if (customerLastName) {
      onConfigurationChange({
        ...configuration,
        customName: `Hautakivi ${customerLastName}`
      });
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder components
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = activeComponents.findIndex(c => c.id === active.id);
      const newIndex = activeComponents.findIndex(c => c.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedActive = arrayMove(activeComponents, oldIndex, newIndex);
        
        // Rebuild components array: reordered active + unchanged available
        const newComponents = [
          ...reorderedActive,
          ...availableComponents
        ];

        onConfigurationChange({
          ...configuration,
          components: newComponents
        });
      }
    }
  }, [activeComponents, availableComponents, configuration, onConfigurationChange]);

  return (
    <div className="space-y-6">
      {/* Header with custom name input */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <Wrench className="h-5 w-5 text-amber-600 mt-1" />
        <div className="flex-1 space-y-3">
          {/* Nimikenttä */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-sm text-amber-700 dark:text-amber-300 mb-1 block">
                Tuotteen nimi (valinnainen)
              </Label>
              <Input
                value={configuration.customName || ""}
                onChange={(e) => handleCustomNameChange(e.target.value)}
                placeholder={
                  customerLastName 
                    ? `esim. Hautakivi ${customerLastName}` 
                    : "esim. Hautakivi Mallikas, Erikoistyö..."
                }
                className="bg-white dark:bg-background border-amber-300 dark:border-amber-700 focus:border-amber-400"
              />
            </div>
            {/* Smart Default -nappi */}
            {customerLastName && !configuration.customName && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSuggestName}
                className="shrink-0 border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/30"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Ehdota
              </Button>
            )}
          </div>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Valitse vapaasti mitä komponentteja tilaus sisältää
          </p>
        </div>
      </div>

      {/* Validation warnings */}
      {warnings.length > 0 && (
        <CustomTemplateWarning warnings={warnings} />
      )}

      {/* Active components with drag and drop */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <Label className="text-base font-semibold">Valitut komponentit</Label>
          <Badge variant="secondary">{activeComponents.length} kpl</Badge>
          {activeComponents.length > 1 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <GripVertical className="h-3 w-3" />
              Vedä järjestääksesi
            </span>
          )}
        </div>

        {activeComponents.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeComponents.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {activeComponents.map(component => {
                  // Compute instance label if multiple of same type
                  const sameTypeActive = activeComponents.filter(
                    c => c.componentTypeId === component.componentTypeId
                  );
                  const instanceLabel = sameTypeActive.length > 1
                    ? `${component.componentTypeLabel} #${sameTypeActive.indexOf(component) + 1}`
                    : undefined;

                  return (
                    <SortableComponentCard
                      key={component.id}
                      component={component}
                      instanceLabel={instanceLabel}
                      onRemove={() => handleRemoveComponent(component.id)}
                      onQuantityChange={(qty) => handleQuantityChange(component.id, qty)}
                      onMaterialChange={(matId) => handleMaterialChange(component.id, matId)}
                      onDimensionChange={(dim, val) => handleDimensionChange(component.id, dim, val)}
                      onAttributeChange={(attrId, val) => handleAttributeChange(component.id, attrId, val)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ei vielä valittuja komponentteja</p>
            <p className="text-sm">Lisää komponentteja alta</p>
          </div>
        )}
      </div>

      {/* Import from product model */}
      <ModelImporter
        onImport={(importedComponents) => {
          onConfigurationChange({
            ...configuration,
            components: [...configuration.components, ...importedComponents],
          });
        }}
      />

      {/* Component picker */}
      <ComponentPicker
        availableComponents={availableComponents}
        activeComponentTypeIds={activeComponentTypeIds}
        onAddComponent={handleAddComponent}
      />
    </div>
  );
}
